// MANAGED-POLICY (build order 2026-10-28 "Managed mode: clients can't add agents"): in a managed
// company the Add agent / Add DE controls give way to "Request an agent", a modal that files a
// request (`POST …/agent-requests`) for the agency to fulfil; the requests list on Setup › Digital
// employees shows their state; an agency admin switches the company between managed and
// self-serve there. `GET …/agent-mode` answers `can_add` (self-serve, or an agency operator) and
// `can_change` (an agency admin), so the screens never guess who may.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Textarea,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SendIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { formatDate } from "../format";

export const agentRequestKeys = {
  mode: (co: string) => ["company", co, "agent-mode"] as const,
  list: (co: string) => ["company", co, "agent-requests"] as const,
  operator: () => ["operator", "agent-requests"] as const,
};

export const agentModeQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: agentRequestKeys.mode(co),
    queryFn: () => api.client.agentMode.get({ params: { co } }),
    staleTime: 60_000,
  });

/**
 * Who adds agents here. `ready` is false until the mode is read: add controls stay hidden until
 * then, so nobody is offered an Add the API would refuse.
 */
export function useAgentMode(api: Api, company: CompanyAccess) {
  const q = useQuery(agentModeQuery(api, company.id));
  return {
    ready: q.isSuccess,
    managed: q.data?.mode === "managed",
    canAdd: q.data?.can_add ?? false,
    canChange: q.data?.can_change ?? false,
  };
}

/** The v1 role catalog (agent-templates design §5): suggestions; any role may be typed. */
export const CATALOG_ROLES: readonly { key: string; name: string }[] = [
  { key: "executive-assistant", name: "Executive assistant" },
  { key: "admin-assistant", name: "Admin assistant" },
  { key: "chief-of-staff", name: "Chief of staff" },
  { key: "sdr-lead-research", name: "SDR / lead research" },
  { key: "social-media-publisher", name: "Social media publisher" },
  { key: "customer-support-first-line", name: "Customer support (first line)" },
  { key: "bookkeeper-assistant", name: "Bookkeeper assistant" },
  { key: "recruiter-coordinator", name: "Recruiter coordinator" },
];

type Kind = "de" | "ag";

/** Who may ask for which kind: a DE is a manager's ask; an AG anyone's who may have one. */
const kindsFor = (company: CompanyAccess): Kind[] =>
  isManager(company) ? ["de", "ag"] : company.roles.includes("member") ? ["ag"] : [];

/** "Request an agent": the add control of a managed company (icon in captions, labelled in headers). */
export function RequestAgentButton({
  api,
  company,
  kind,
  variant,
  size = "sm",
}: {
  api: Api;
  company: CompanyAccess;
  kind: Kind;
  variant: "icon" | "labelled";
  size?: "sm" | "lg";
}) {
  const [open, setOpen] = useState(false);
  const kinds = kindsFor(company);
  if (!kinds.includes(kind)) return null;
  const label = kind === "de" ? "Request a digital employee" : "Request an agent";
  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={label}
          title={label}
          data-slot="request-agent"
          onClick={() => setOpen(true)}
        >
          <SendIcon aria-hidden="true" />
        </Button>
      ) : (
        <Button size={size} data-slot="request-agent" onClick={() => setOpen(true)}>
          <SendIcon aria-hidden="true" />
          {label}
        </Button>
      )}
      {open ? (
        <RequestAgentDialog
          api={api}
          company={company}
          kinds={kinds}
          initialKind={kind}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

export function RequestAgentDialog({
  api,
  company,
  kinds,
  initialKind,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  kinds: readonly Kind[];
  initialKind: Kind;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>(initialKind);
  const [role, setRole] = useState("");
  const [note, setNote] = useState("");
  const templateKey = CATALOG_ROLES.find(
    (r) => r.name.toLowerCase() === role.trim().toLowerCase(),
  )?.key;
  const send = useMutation({
    mutationFn: () =>
      api.client.agentRequests.create({
        params: { co: company.id },
        body: {
          kind,
          role: role.trim(),
          ...(templateKey ? { template_key: templateKey } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: agentRequestKeys.list(company.id) });
      toast.add({ title: "Request sent to your agency", type: "success" });
      onClose();
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The request was not sent."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!role.trim()) return;
    send.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-slot="request-agent-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              Request an agent
              <InfoTip label="About requests">
                Your agency adds agents for {company.name}. Send what you need; they set it up from
                their templates and you hear back in your inbox.
              </InfoTip>
            </DialogTitle>
            <DialogDescription>Your agency sets it up.</DialogDescription>
          </DialogHeader>
          {kinds.length > 1 ? (
            <Field>
              <FieldLabel htmlFor={`${id}-kind`} className="flex items-center gap-1">
                Kind
                <InfoTip label="About DE and AG">
                  A digital employee (DE) holds a seat with goals, KPIs and a budget. An agent (AG)
                  runs tasks for a DE or a person.
                </InfoTip>
              </FieldLabel>
              <NativeSelect
                id={`${id}-kind`}
                value={kind}
                onChange={(e) => setKind(e.target.value as Kind)}
              >
                <NativeSelectOption value="de">Digital employee (DE)</NativeSelectOption>
                <NativeSelectOption value="ag">Agent (AG)</NativeSelectOption>
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel htmlFor={`${id}-role`}>Role</FieldLabel>
            <Input
              id={`${id}-role`}
              list={`${id}-roles`}
              value={role}
              maxLength={200}
              placeholder="Executive assistant"
              onChange={(e) => setRole(e.target.value)}
              autoFocus
              required
            />
            <datalist id={`${id}-roles`}>
              {CATALOG_ROLES.map((r) => (
                <option key={r.key} value={r.name} />
              ))}
            </datalist>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-note`}>Note</FieldLabel>
            <Textarea
              id={`${id}-note`}
              rows={3}
              maxLength={2000}
              value={note}
              placeholder="What it should handle, tools it needs, who it works for"
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!role.trim() || send.isPending}>
              {send.isPending ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATE_BADGE = {
  open: { label: "Open", variant: "warning" },
  fulfilled: { label: "Fulfilled", variant: "success" },
  declined: { label: "Declined", variant: "neutral" },
} as const;

/** A helper request's stage, as its client reads it (helper-requests.tsx has the full map). */
const HELPER_STAGE_LABEL = {
  requested: "Requested",
  quoted: "Quote ready",
  approved: "Approved",
  paid: "Paid",
  building: "Building",
  added: "Added",
  declined: "Declined",
} as const;

export function RequestState({ state }: { state: keyof typeof STATE_BADGE }) {
  const s = STATE_BADGE[state];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

/** The company's requests (managers: all; others: their own), newest first. */
export function AgentRequestsSection({
  api,
  company,
  className,
}: {
  api: Api;
  company: CompanyAccess;
  className?: string;
}) {
  const list = useQuery({
    queryKey: agentRequestKeys.list(company.id),
    queryFn: () => api.client.agentRequests.list({ params: { co: company.id } }),
  });
  const rows = list.data?.data ?? [];
  if (!list.isSuccess || rows.length === 0) return null;
  return (
    <section
      className={["flex flex-col gap-2", className].filter(Boolean).join(" ")}
      data-slot="agent-requests"
    >
      <h2 className="flex items-center gap-1 text-sm font-medium">
        Requests
        <InfoTip label="About requests">
          Requests for agents sent to your agency, and what became of them.
        </InfoTip>
      </h2>
      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
            <span className="font-medium">{r.role}</span>
            <span className="text-muted-foreground">
              {r.kind === "de" ? "DE" : r.kind === "helper" ? "Helper" : "AG"} ·{" "}
              {r.requested_by_name} · {formatDate(r.created_at.slice(0, 10))}
            </span>
            <span className="ml-auto flex items-center gap-2">
              {r.resolution_note ? (
                <InfoTip label="Agency note">{r.resolution_note}</InfoTip>
              ) : null}
              {r.stage ? (
                // HELPERS: a paid helper request shows its stage (approved on the DE's Helpers tab).
                <Badge
                  variant={
                    r.stage === "added" ? "success" : r.stage === "quoted" ? "warning" : "neutral"
                  }
                >
                  {HELPER_STAGE_LABEL[r.stage]}
                </Badge>
              ) : (
                <RequestState state={r.state} />
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** An agency admin's switch: managed (requests) or self-serve (the company adds agents). */
export function AgentModeSwitch({ api, company }: { api: Api; company: CompanyAccess }) {
  const id = useId();
  const qc = useQueryClient();
  const mode = useQuery(agentModeQuery(api, company.id));
  const put = useMutation({
    mutationFn: (managed: boolean) =>
      api.client.agentMode.put({
        params: { co: company.id },
        body: { mode: managed ? "managed" : "self_serve" },
      }),
    onSuccess: (v) => {
      qc.setQueryData(agentRequestKeys.mode(company.id), v);
      toast.add({
        title:
          v.mode === "managed" ? "Managed: requests only" : "Self-serve: the company adds agents",
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The mode was not changed."), type: "error" }),
  });
  if (!mode.data?.can_change) return null;
  return (
    <div className="flex items-center gap-2 text-sm" data-slot="agent-mode-switch">
      <Switch
        id={`${id}-managed`}
        checked={mode.data.mode === "managed"}
        disabled={put.isPending}
        onCheckedChange={(on) => put.mutate(on)}
      />
      <label htmlFor={`${id}-managed`}>Managed</label>
      <InfoTip label="About managed mode">
        On: {company.name} requests agents and your agency adds them. Off: its owners and admins add
        agents themselves.
      </InfoTip>
    </div>
  );
}
